import { cn } from '@/lib/cn';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import type { OrgAssignmentDetail, OrgAssignmentSubmissionRow } from '@/lib/api/org';

const STATUS_STYLES: Record<OrgAssignmentSubmissionRow['status'], string> = {
  submitted: 'bg-[#f0fdf4] text-[#16a34a]',
  late: 'bg-[#fffbeb] text-[#d97706]',
  pending: 'bg-[#f3f4f6] text-[#6b7280]',
};

function StatusBadge({ status }: { status: OrgAssignmentSubmissionRow['status'] }) {
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return <span className={cn('inline-flex items-center rounded-full px-3 py-1 text-xs font-medium', STATUS_STYLES[status])}>{label}</span>;
}

export function AssignmentDetailView({ data }: { data: OrgAssignmentDetail }) {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[28px] font-bold leading-tight text-[#111827]">{data.title}</h1>
        <span className={cn(
          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
          data.kind === 'project' ? 'bg-[#eff6ff] text-[#2563eb]' : 'bg-[#f3f4f6] text-[#6b7280]',
        )}>
          {data.kind === 'project' ? 'Project' : 'Assignment'}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#6b7280]">
        {data.due_date && <span>Due {data.due_date}</span>}
        <span>Max score {data.max_score}</span>
      </div>

      <OrgCard className="overflow-hidden">
        {data.submissions.length === 0 ? (
          <p className="py-16 text-center text-sm text-[#9ca3af]">No students enrolled in this batch.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr className="border-b border-[#ececec]">
                  {['Student', 'Status', 'Score', 'Submitted'].map((h, i) => (
                    <th key={i} className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.submissions.map((s, i) => (
                  <tr key={s.user_id} className={cn(i !== data.submissions.length - 1 && 'border-b border-[#ececec]')}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <OrgAvatar name={s.name} />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-[#111827]">{s.name}</p>
                          <p className="truncate text-xs text-[#9ca3af]">{s.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4"><StatusBadge status={s.status} /></td>
                    <td className="px-6 py-4 text-sm tabular-nums text-[#374151]">
                      {s.status === 'pending' ? '—' : `${Math.round(s.score)}/${data.max_score}`}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#374151]">{s.submitted_at || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </OrgCard>
    </div>
  );
}
